import { ColumnDef } from '@tanstack/react-table';
import { LogConfig, LogSearchFieldsType, LogTypeEnum } from '@Types/log';
export const logColumns: { [type: string]: ColumnDef<any>[] } = {
  detection: [],
  audit: [],
  inlineTracking: [],
  quarantine: [],
  apiIntegration: [],
  urlTracking: [],
};
const config: LogConfig = {
  detection: {
    //four in one, data_protection\virtual_analyzer\ransomware\security_risk_scan
    //TODO: merge four type' config, now just security_risk_scan
    key: 'detection',
    showFields: [
      'timestamp',
      'orgName',
      'scanSource',
      'securityFilterName',
      'threatType',
      'mergedSecurityRiskName',
      'malwareDetectedBy',
      'wrsRiskLevel',
      'ddaaasRiskLevel', // virtual_analyzer
      'violator',
      'mergedWritingStyleCompromisedSender',
      'policy',
      'action',
      'mdDetectStatus',
      'mailDirection',
      'teamsName',
      'mergedFieldFoundIn',
      'fileName',
      'fileSha1',
      'fileSha256',
      'msgSubject',
      'msgFrom',
      'smtpSender', //related to inline services provisioned
      'msgTo',
      'msgSubmitTime',
      'fileUploadTime',
      'scanType',
      // 'spamSnapReport',   // not show
      'fileVaSummary', // virtual_analyzer ?
      'internetMessageId',
      'productServerName',
      'tmaseXheaders',
      'totalProcessingTime', // related to globalSetting.logProcessingTimeEnabled
      'ddaaasScanType', // virtual_analyzer
      'dlpTemplate', //data_protection
      'restoreReason',
      'dlpForensicDataContent',
    ],
    facets: [
      'companyGuid',
      'scanSource',
      'securityFilterName',
      'threatType',
      'malwareDetectedBy',
      'spamCategory',
      'urlCategory',
      'suspiciousUrl',
      'suspiciousDomain',
      'ddaaasScanType',
      'ddaaasRiskLevel',
      'violator',
      'policy',
      'action',
      'mailDirection',
      'msgFrom',
      'dlpTemplate',
      'dlpMatchedLabels',
    ],
    searchFields: [
      'companyGuid', //Organization (mapped from orgid, search_type: "query_string")
      'scanSource', //Scan Source
      'securityFilterName', //Security Filter
      'threatType', //Threat Type
      'mergedSecurityRiskName', //Security Risk Name (merged from ["blockReason", "suspiciousUrl", "spamCategory", "fbTrueFileType", "fileName"]) ??
      'malwareDetectedBy', //Detected by
      'wrsRiskLevel', //Risk Level
      'violator', //Affected User
      'mergedWritingStyleCompromisedSender', //Supposed Sender (merged from ["tm_spam_expected_sender", "tm_spam_expected_sender_displayname"])  ??
      'policy', //Triggered Policy
      'action', //Action
      'mailDirection', //Mail Direction
      'mergedFieldFoundIn', //Location  (merged from "folderPath")  ??
      'fileName', //File Name
      'fileSha1', //SHA-1
      'fileSha256', //SHA-256
      'msgSubject', //Subject
      'msgFrom', //Sender
      'smtpSender', //Envelope Sender
      'msgTo', //Recipient
      'msgSubmitTime', //Message Arrival Time (search_type: "range", values: ["2023-02-10T16:00", "2023-02-10T16:00"])
      'fileUploadTime', //File Modification Time (search_type: "range")
      'scanType', //Scan Type
      'internetMessageId', //Message ID
      'productServerName', //ScanMail Server Hostname
      'ddaaasScanType', //Detection Type (VA only)
      'dlpTemplate', //Triggered Template (DLP only)
      'dlpMatchedLabels', //Triggered Label (DLP only)
      'all', //All Fields  ??
    ],
    mergedFields: {
      // for security_risk_scan
      mergedSecurityRiskName: [
        'blockReason',
        'suspiciousUrl',
        'spamCategory',
        'fbTrueFileType',
        'fileName',
        'urlCategory',
      ],
      mergedFieldFoundIn: [
        'teamsChatRecipient',
        'teamsChatIdentity',
        'teamsChatGroupParticipants',
        'folderPath',
      ],
      mergedWritingStyleCompromisedSender: [
        'spamExpectedSender',
        'spamExpectedSenderDisplayName',
      ],
    },
    wrapFields: [
      'blockReason',
      'suspiciousUrl',
      'spamCategory',
      'fbTrueFileType',
      'msgTo',
      'msgFrom',
      'tmaseXheaders',
      'dlpTemplate',
      'dlpForensicDataContent',
    ],
  },
  audit: {
    key: 'audit',
    showFields: ['timestamp', 'user', 'action', 'message'],
    facets: ['companyGuid', 'user', 'action'],
    searchFields: [
      'companyGuid',
      'user',
      'action',
      'message',
      'headerFrom',
      'recipient',
      'subject',
      'messageId',
      'mailbox',
      'folder',
      'all',
    ],
    mergedFields: {},
    wrapFields: [],
  },
  inlineTracking: {
    key: 'inlineTrackingLogs',
    showFields: [
      'timestamp',
      'orgName',
      'scanSource',
      'msgMimeSender',
      'msgSender',
      'recipients',
      'inTime',
      'deliveredStatus',
      'senderIp',
      'clientSenderIp',
      'deliveredTo',
      'outTime',
      'mailDirection',
      'msgSubject',
      'messageId',
      'reason',
    ],
    facets: [
      'companyGuid',
      'scanSource',
      'deliveredStatus',
      'recipients',
      'mailDirection',
    ],
    searchFields: [
      'companyGuid', //Organization (mapped from orgid, search_type: "query_string")
      'scanSource',
      'msgMimeSender', //Sender
      'msgSender', //Envelope Sender
      'recipients', //Recipient
      'inTime', //Received At
      'deliveredStatus', //Delivery Status
      'senderIp', //Received From
      'clientSenderIp', //Sender IP
      'deliveredTo', //Delivered To
      'outTime', //Delivered At
      'mailDirection', //Mail Direction
      'msgSubject', //Subject
      'messageId', //Message ID
      'reason', //Reason
      'all', //All Fields
    ],
    mergedFields: {},
    wrapFields: [],
  },
  quarantine: {
    key: 'quarantine',
    showFields: [
      'timestamp',
      'orgName',
      'scanSource',
      'filterType',
      'threatType',
      'mergedSecurityRiskName',
      'violator',
      'mergedFieldFoundIn',
      'mergedFieldFileName',
      'msgSubject',
      'msgFrom',
      'msgTo',
      'mailDirection',
      'teamsName',
      'quarantineType',
      'fileStatus',
      'restoreBy',
      'restoreReason',
      'fileUploadTime',
      'scanType',
      'messageId',
      'productServerName',
      'protectionMode',
    ],
    facets: [
      'companyGuid',
      'scanSource',
      'filterType',
      'threatType',
      'violator',
      'quarantineType',
      'restoreBy',
      'mailDirection',
    ],
    searchFields: [
      'companyGuid', //Organization (mapped from orgid, search_type: "query_string")
      'scanSource', //Scan Source
      'securityFilterName', //Security Filter
      'threatType',
      'mergedSecurityRiskName', //Security Risk Name (merged from ["blockReason", "suspiciousUrl", "spamCategory", "fbTrueFileType", "fileName"])
      'violator', //Affected User
      'mergedFieldFoundIn', //Location (Exchange Online folder, then original file path)
      'fileName', //File Name
      'msgSubject', //Subject
      'msgFrom', //Sender
      'msgTo', //Recipient
      'mailDirection', //Mail Direction
      'quarantineType', //Quarantine Type
      'fileStatus', //Status
      'fileUploadTime', //File Modification Time (search_type: "range")
      'scanType', //Scan Type
      'messageId', //Message ID
      'productServerName', //ScanMail Server Hostname
      'protectionMode', //Protection Mode
      'all', //All Fields
    ],
    mergedFields: {
      mergedSecurityRiskName: [
        'blockReason',
        'suspiciousUrl',
        'spamCategory',
        'fbTrueFileType',
        'fileName',
        'urlCategory',
      ],
      mergedFieldFoundIn: ['fileOriginalPath', 'folderPath'],
      mergedFieldFileName: ['violatedFile', 'fileName'],
    },
    wrapFields: [
      'msgTo',
      'msgFrom',
      'blockReason',
      'suspiciousUrl',
      'spamCategory',
      'fbTrueFileType',
    ],
  },
  apiIntegration: {
    key: 'apiIntegration',
    showFields: [
      'timestamp',
      'orgName',
      'scanSource',
      'securityFilterName',
      'mergedSecurityRiskName',
      'violator',
      'action',
      'mailDirection',
      'mergedFieldFoundIn',
      'fileName',
      'msgSubject',
      'msgFrom',
      'msgTo',
      'msgSubmitTime',
      'scanType',
    ],
    facets: [
      'companyGuid',
      'scanSource',
      'securityFilterName',
      'violator',
      'action',
      'mailDirection',
    ],
    searchFields: [
      'companyGuid', //Organization (mapped from orgid, search_type: "query_string")
      'scanSource', //Scan Source
      'securityFilterName', //Security Filter
      'mergedSecurityRiskName', //Security Risk Name (merged from ["blockReason", "suspiciousUrl", "spamCategory", "fbTrueFileType", "fileName"])
      'violator', //Affected User
      'mergedWritingStyleCompromisedSender', //Supposed Sender
      'action', //Action
      'mailDirection', //Mail Direction
      'mergedFieldFoundIn', //Location  (merged from "folderPath") ??
      'fileName', //File Name
      'msgSubject', //Subject
      'msgFrom', //Sender
      'msgTo', //Recipient
      'msgSubmitTime', //Message Arrival Time (search_type: "range")
      'scanType', //Scan Type
      'all', //All Fields
    ],
    mergedFields: {
      mergedSecurityRiskName: [
        'blockReason',
        'suspiciousUrl',
        'fileSha1',
        'fileSha256',
        'msgFrom',
        'urlCategory',
      ],
      mergedFieldFoundIn: ['folderPath'],
    },
    wrapFields: ['suspiciousUrl', 'msgTo', 'msgFrom'],
  },
  urlTracking: {
    key: 'urlTracking',
    showFields: [
      'timestamp',
      'action',
      'sender',
      'recipients',
      'url',
      'messageId',
      'violator',
    ],
    facets: ['sender', 'recipients', 'url', 'messageId', 'violator', 'all'],
    searchFields: [
      'action',
      'sender', //Sender
      'recipients', //Recipient
      'url', //URL
      'messageId', //Message ID
      'violator', //Affected User
    ],
    mergedFields: {}, //none
    wrapFields: [], //none
  },
};
const rangeFields = ['msgSubmitTime', 'fileUploadTime'];
export const getLogSearchFields = () => {
  const searchFields: LogSearchFieldsType = {
    detection: [],
    audit: [],
    inlineTracking: [],
    quarantine: [],
    apiIntegration: [],
    urlTracking: [],
  };
  Object.keys(config).forEach((logType) => {
    const logConfig = config[logType as LogTypeEnum];
    searchFields[logType as LogTypeEnum] = logConfig.searchFields.map(
      (field: string) => {
        const item = {
          field,
          type: 'fuzzyMatching',
        };
        if (~rangeFields.indexOf(field)) {
          item.type = 'range';
        }
        return item;
      }
    );
  });
  return searchFields;
};

export const getFacetFields = () => {
  const facetFields: LogSearchFieldsType = {
    detection: [],
    audit: [],
    inlineTracking: [],
    quarantine: [],
    apiIntegration: [],
    urlTracking: [],
  };
  Object.keys(config).forEach((logType) => {
    const logConfig = config[logType as LogTypeEnum];
    facetFields[logType as LogTypeEnum] = logConfig.facets.map((field) => {
      const item = {
        field,
        values: [],
        type: 'terms',
      };
      return item;
    });
  });
  return facetFields;
};

export const mappingFields = [
  'fileStatus',
  'quarantineType',
  'filterType',
  'action',
  'ddaaasRiskLevel',
  'scanSource',
  'securityFilterName',
  'scanType',
  'urlCategory',
  'errorCode',
  'ddaaasScanType',
  'wrsRiskLevel',
  // 'spamCategory',  this field handle conflict with wrapLogFieldFuncMap["spamCategory"]
  'appTypeValue',
  'eventCategoryValue',
  'eventTypeValue',
  'fileTypeCategoryValue',
  'malwareDetectedBy',
  'restoreBy',
  'riskPreventionType',
  'policySeverity',
  'actionSource',
  //'messageId',
  //'productServerName',
  'mailDirection',
  'deliveredStatus',
  'protectionMode',
  'threatType',
  'mdDetectStatus',
] as const;

export const quishingMappingFields = ['mergedSecurityRiskName'];

// { facetName: {l10nValue: field}}
// export const mappingFieldsMap = () => {

// };

export const urlMaskFields = [
  'suspiciousDomain',
  'suspiciousUrl',
  'blockReason',
];

export const timeFields = [
  'timestamp',
  'msgDeliveryTime',
  'fileUploadTime',
  'msgSubmitTime',
  'eventHappenAt',
  'detectedAt',
  'lastAccessed',
  'inTime',
  'outTime',
] as const;

export const noNeedSortFields = [
  'violator',
  'mergedFieldFoundIn',
  'spamSnapReport',
  'malwareReport',
  'mergedSecurityRiskName',
  'fileVaSummary',
  'dlpForensicDataContent',
  'mergedFieldFileName',
  'restoreReason',
  'message',
  'user_email',
  'event_description',
  'user_displayname',
  'file_meta_name',
  'file_meta_parent_folder',
  'risk_description',
  'event_ids',
  'restoreReason',
  'mergedWritingStyleCompromisedSender',
  'salesforceViolatedFields',
  'salesforceTypeName',
  'service_genre',
  'sanction_status',
  'messageId',
  'tmaseXheaders',
  'internetMessageId',
  'action',
  'sender',
  'messageId',
  'recipients',
];

export const noNeedShowFields = [
  'spamSnapReport',
  'activity_count',
  'policy_window_time',
  'policy_window_unit',
  'anomaly_type',
  'malwareReport',
  'abnormal_login_city',
  'abnormal_login_country',
  'event_id',
  'last_login_at',
  'last_login_from_country_name',
  'last_login_from_city_name',
  'last_login_from_client_ip',
  'anomaly_id',
  'policy_id',
];

export const logArrayFields = [
  'dlpRule',
  'dlpTemplate',
  'blockReason',
  'spamCategory',
  'urlCategory',
  'msgFrom',
  'msgTo',
  'tmaseXheaders',
] as const;

export const policyActions = [
  'ADD_POLICY',
  'UPDATE_POLICY',
  'DELETE_POLICY',
  'COPY_POLICY',
  'REORDER_POLICY',
  'ENABLE_POLICY',
  'DISABLE_POLICY',
  'ENABLE_ADVANCED_SPAM_PROTECTION',
  'DISABLE_ADVANCED_SPAM_PROTECTION',
  'ENABLE_FILE_BLOCKING',
  'DISABLE_FILE_BLOCKING',
  'ENABLE_DATA_LOSS_PREVENTION',
  'DISABLE_DATA_LOSS_PREVENTION',
  'ENABLE_WEB_REPUTATION',
  'DISABLE_WEB_REPUTATION',
  'ENABLE_VIRTUAL_ANALYZER',
  'DISABLE_VIRTUAL_ANALYZER',
  'ENABLE_ADVANCED_SPAM_PROTECTION_APPROVED_SENDER_LIST',
  'DISABLE_ADVANCED_SPAM_PROTECTION_APPROVED_SENDER_LIST',
  'ENABLE_ADVANCED_SPAM_PROTECTION_BLOCK_SENDER_LIST',
  'DISABLE_ADVANCED_SPAM_PROTECTION_BLOCK_SENDER_LIST',
  'ENABLE_WEB_REPUTATION_APPROVED_SENDER_LIST',
  'DISABLE_WEB_REPUTATION_APPROVED_SENDER_LIST',
  'ENABLE_VIRTUAL_ANALYZER_APPROVED_SENDER_LIST',
  'DISABLE_VIRTUAL_ANALYZER_APPROVED_SENDER_LIST',
  'ENABLE_WEB_REPUTATION_APPROVED_URL_LIST',
  'DISABLE_WEB_REPUTATION_APPROVED_URL_LIST',
  'ENABLE_WEB_REPUTATION_BLOCK_URL_LIST',
  'DISABLE_WEB_REPUTATION_BLOCK_URL_LIST',
  'ENABLE_ADVANCED_SPAM_PROTECTION_APPROVED_HEADER_FIELD',
  'DISABLE_ADVANCED_SPAM_PROTECTION_APPROVED_HEADER_FIELD',
  'ENABLE_WEB_REPUTATION_APPROVED_HEADER_FIELD',
  'DISABLE_WEB_REPUTATION_APPROVED_HEADER_FIELD',
  'ENABLE_DATA_LOSS_PREVENTION_KEYWORD_EXTRACTION',
  'DISABLE_DATA_LOSS_PREVENTION_KEYWORD_EXTRACTION',
  'UPDATE_ADVANCED_SPAM_PROTECTION_DETECTION_LEVEL',
  'UPDATE_ADVANCED_SPAM_PROTECTION_APPLY_TO',
  'UPDATE_MALWARE_SCANNING_APPLY_TO',
  'UPDATE_FILE_BLOCKING_APPLY_TO',
  'UPDATE_WEB_REPUTATION_APPLY_TO',
  'UPDATE_VIRTUAL_ANALYZER_APPLY_TO',
  'ADD_TEMPLATE',
  'UPDATE_TEMPLATE',
  'COPY_TEMPLATE',
  'DELETE_TEMPLATE',
  'ADD_KEYWORD',
  'UPDATE_KEYWORD',
  'COPY_KEYWORD',
  'DELETE_KEYWORD',
  'ADD_EXPRESSION',
  'UPDATE_EXPRESSION',
  'COPY_EXPRESSION',
  'DELETE_EXPRESSION',
  'INLINE_TRANSPORT_RULE_DELETE',
  'INLINE_TRANSPORT_RULE_CREATE',
  'INLINE_INCOMING_O365_GROUP_CREATE',
  'INLINE_INCOMING_O365_GROUP_UPDATE',
  'INLINE_INCOMING_O365_GROUP_DELETE',
  'INLINE_INCOMING_TRANSPORT_RULE_CREATE',
  'INLINE_INCOMING_TRANSPORT_RULE_UPDATE',
  'INLINE_INCOMING_TRANSPORT_RULE_DELETE',
  'INLINE_INCOMING_CONNECTOR_CREATE',
  'INLINE_INCOMING_CONNECTOR_UPDATE',
  'INLINE_INCOMING_CONNECTOR_DELETE',
  'INLINE_INCOMING_CONNECTOR_DELETE',
  'INLINE_OUTGOING_O365_GROUP_CREATE',
  'INLINE_OUTGOING_O365_GROUP_UPDATE',
  'INLINE_OUTGOING_O365_GROUP_DELETE',
  'INLINE_OUTGOING_TRANSPORT_RULE_CREATE',
  'INLINE_OUTGOING_TRANSPORT_RULE_UPDATE',
  'INLINE_OUTGOING_TRANSPORT_RULE_DELETE',
  'INLINE_OUTGOING_CONNECTOR_CREATE',
  'INLINE_OUTGOING_CONNECTOR_UPDATE',
  'INLINE_OUTGOING_CONNECTOR_DELETE',
  'UPDATE_ADVANCED_SPAM_PROTECTION_APPROVED_LIST',
  'UPDATE_ADVANCED_SPAM_PROTECTION_BLOCKED_LIST',
  'UPDATE_MALWARE_SCANNING_ACTIVE_CONTENT_EXCEPT_LIST',
  'UPDATE_FILE_BLOCKING_APPROVED_LIST',
  'ENABLE_ADVANCED_SPAM_PROTECTION_BLOCKED_HEADER_FIELD',
  'DISABLE_ADVANCED_SPAM_PROTECTION_BLOCKED_HEADER_FIELD',
  'UPDATE_WEB_REPUTATION_APPROVED_LIST',
  'UPDATE_WEB_REPUTATION_BLOCKED_LIST',
  'UPDATE_VIRTUAL_ANALYZER_APPROVED_LIST',
  'ENABLE_VIRTUAL_ANALYZER_APPROVED_FILE_NAME',
  'UPDATE_SHARE_LINK_EXCEPTION_LIST',
  'UPDATE_DATA_LOSS_PREVENTION_APPROVED_LIST',
  'ADD_SHAREPOINT_ONLINE_APPROVED_SITES',
  'DELETE_SHAREPOINT_ONLINE_APPROVED_SITES',
  'ENABLE_CORRELATED_INTELLIGENCE',
  'DISABLE_CORRELATED_INTELLIGENCE',
  'ENABLE_CORRELATED_INTELLIGENCE_AGENTIC_AI',
  'DISABLE_CORRELATED_INTELLIGENCE_AGENTIC_AI',
  'ENABLE_CORRELATED_INTELLIGENCE_APPROVED_LIST',
  'DISABLE_CORRELATED_INTELLIGENCE_APPROVED_LIST',
  'UPDATE_CORRELATED_INTELLIGENCE_APPROVED_LIST',
  'CORRELATED_INTELLIGENCE_ANOMALIES_PRE_DEFINED_SCOPE_ALL',
  'CORRELATED_INTELLIGENCE_ANOMALIES_PRE_DEFINED_SCOPE_SPECIFIC',
  'UPDATE_CORRELATED_INTELLIGENCE_ANOMALIES_RULES',
  'UPDATE_CORRELATED_INTELLIGENCE_ANOMALIES_EXP_RULES',
  'UPDATE_OTHER_POLICY_DETAILS',
];

export const provisionActions = [
  'ADD_DELEGATE_ACCOUNT',
  'DELETE_DELEGATE_ACCOUNT',
  'DELETE_SERVICE_ACCOUNT',
  'EXTEND_EXCHANGE_SERVER_EXPIRATION',
  'UPDATE_DELEGAT_ACCOUNT',
  'MANUALLY_ADD_DELEGATE_ACCOUNT',
  'SEND_GLOBAL_ADMIN',
  'MIGRATE_SERVICE_ACCOUNT',
  'EXTEND_365_TARGET_PROTECTION',
  'DELETE_DELEGATOR_ACCOUNTS',
  'DELETE_RMS_ACCOUNT',
  'ADD_RMS_ACCOUNT',
  'ADD_SERVICE_ACCOUNT',
  'RECREATE_TOKEN',
  'MIGRATE_OUTBOUND',
  'UPGRADE_FOR_OUTBOUND',
  'RECREATE_ACCESS_TOKEN',
];

export const idSyncActions = ['UPDATE_USERS_AND_GROUPS'];
export const scheduleSyncActions = [
  'EXCHANGE_SCHEDULED_SYNC',
  'SHAREPOINT_SCHEDULED_SYNC',
  'ONEDRIVE_SCHEDULED_SYNC',
  'BOX_SCHEDULED_SYNC',
  'DROPBOX_SCHEDULED_SYNC',
  'SALESFORCE_PRODUCTION_SCHEDULED_SYNC',
  'SALESFORCE_SANDBOX_SCHEDULED_SYNC',
  'GOOGLEDRIVE_SCHEDULED_SYNC',
  'GMAIL_SCHEDULED_SYNC',
  'MICROSOFT_TEAMS_SCHEDULED_SYNC',
  'MICROSOFT_TEAMS_CHAT_SCHEDULED_SYNC',
  'INFOMATION_PROTECTION_SCHEDULED_SYNC',
];
export const agentConnectionActions = [
  'DISCONNECT_FROM_XDR',
  'CONNECT_TO_XDR',
  'ADD_AUTOMATION_API_AUTHENTICATION_TOKEN',
  'DELETE_AUTOMATION_API_AUTHENTICATION_TOKEN',
  'EDIT_AUTOMATION_API_AUTHENTICATION_TOKEN',
  'REFRESH_AUTOMATION_API_AUTHENTICATION_TOKEN',
];

export const isoTimeFormat = 'YYYY-MM-DD[T]HH:mm';

// CAS-4153 Misdirected Emails identifiers shared by mock factories and log handlers.
export const MISDIRECTED_SECURITY_FILTER_NAME = 'Misdirected Emails';
export const MISDIRECTED_DETECTED_BY = 'Misdirected Detection';
export const MISDIRECTED_DETECT_STATUS_DEFAULT = 'OnHold';

export const logCopyFields: [string, string][] = [
  ['malwareDetectedBy', 'malwareDetectedByKey'],
  ['malwareReport', 'malwareReport'],
  ['companyGuid', 'companyGuid'],
  ['scanSource', 'scanSourceKey'],
  ['ddaaasRiskLevel', 'ddaaasRiskLevelKey'],
  ['riskPreventionType', 'riskPreventionTypeOri'],
  ['timestamp', 'timestampUtc'],
  ['ddaaasAnalysisMode', 'ddaaasAnalysisMode'],
  ['headerFrom', 'headerFrom'],
  ['recipient', 'recipient'],
  ['subject', 'subject'],
  ['messageId', 'messageId'],
  ['envelopeFrom', 'envelopeFrom'],
  ['mailbox', 'mailbox'],
  ['folder', 'folder'],
  ['traceId', 'traceId'],
  ['digestBlobPath', 'digestBlobPath'],
  ['dlpRule', 'dlpRule'],
  ['raiMatchedRules', 'raiMatchedRules'],
  ['threatType', 'threatTypeKey'],
  ['securityFilterName', 'securityFilterNameKey'],
  ['raiRiskType', 'raiRiskType'],
  ['mdDetectStatus', 'mdDetectStatusKey'],
  ['mdSessionId', 'mdSessionId'],
];

export default config;
